// src/Components/AdvancedFlowsDashboard.jsx

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import useWebSocketDataStore from '../Store/WebSocketDataStore';
import { useDataState } from '../Hooks/useDataState';
import DataStateBadge from './Common/DataStateBadge';
import useGreeksWebsocketDataStore from '../Store/GreeksWebsocketDataStore';
import usePlaybackStore from '../Store/playbackStore';
import useWidgetMinuteFocusStore, {
  minuteBucketMs,
  useFocusedMinuteSourceCleanup,
} from '../Store/widgetMinuteFocusStore';
import { etDateKey } from '../Utils/marketHours';
import { WidgetPanelControls, WidgetPanelDataStatus } from './Dashboard/WidgetPanelSlot';
import { registerWidgetLayoutLoadController } from './layoutManager';

import { isMobileChart } from './Chart_Support_Files/chartSymbolUtils';
import {
  buildWidgetOptions,
  applyChartStyling,
  attachCustomButtons,
} from './Chart_Support_Files/chartConfig';

import {
  loadDefaultIndicators,
  attachSymbolChangeHandler,
} from './Chart_Support_Files/chartStudyLoader';

import { handleIndicatorReloadCheck } from './Chart_Support_Files/chartReloadUtils';

import { useChartTimeWindowStore } from '../Store/chartTimeWindowStore';
import { useChartPriceWindowStore } from '../Store/chartPriceWindowStore';
import {
  DEFAULT_VISIBLE_SECONDS,
  RESOLUTION_STATUS,
  RIGHT_GUTTER_BARS,
  WINDOW_MODE,
  clampExtentToSessionClose,
  edgeTakesGutter,
  edgeToleranceSeconds,
  extentFromEpochSeconds,
  inferBarIntervalSeconds,
  interactiveRightEdgeSeconds,
  isCustomWindowPreference,
  resolveWindowPreference,
  rightGutterBarsForContentSpan,
  rightGutterBarsForVisibleSpan,
  sessionDomainSeconds,
} from '../Utils/chartTimeWindow';

const WINDOW_SOURCE_ID = 'price-chart';

const mainPriceScale = (chart) => {
  const pane = chart?.getPanes?.().find((candidate) => candidate.hasMainSeries?.());
  return pane?.getMainSourcePriceScale?.() || null;
};

// Blank space past the last print, in bar widths. Doubles as the gutter that
// gives markers near the close room to render whole, and as the ceiling a drag
// snaps back to -- the reported defect was a drag parking the final bar far left
// behind a wide empty after-hours region.
const MAX_RIGHT_OFFSET_BARS = RIGHT_GUTTER_BARS;

// Long enough to cover this library's asynchronous range writes, short enough
// that a real drag right after one still registers.
const PROGRAMMATIC_RANGE_MS = 400;

// The saved-view restore lands after chart-ready and even after dataReady --
// the 1500ms re-assert below exists for exactly that race -- so resets must stay
// suppressed past it rather than for one ordinary programmatic beat.
const RESET_SETTLE_MS = 2000;

// TradingView can accept a range while a tab rebuild drops the write. Verify a
// bounded number of times instead of trusting the unresolved promise.
const WINDOW_APPLY_ATTEMPTS = 4;
const WINDOW_APPLY_RETRY_MS = 250;
const CHART_LOAD_RECOVERY_MS = 10000;
const MAX_CHART_LOAD_RECOVERY_ATTEMPTS = 3;

// How long a wheel or drag on the chart container counts as user intent. Matches
// the Bookmap's window so both surfaces behave the same.
const USER_INTENT_MS = 600;

const sameVisibleRange = (left, right) => Number.isFinite(left?.from)
  && Number.isFinite(left?.to)
  && Number.isFinite(right?.from)
  && Number.isFinite(right?.to)
  && Math.abs(left.from - right.from) <= 1e-6
  && Math.abs(left.to - right.to) <= 1e-6;

const visibleRangeSpanChanged = (left, right) => {
  const leftSpan = Number(left?.to) - Number(left?.from);
  const rightSpan = Number(right?.to) - Number(right?.from);
  return Number.isFinite(leftSpan)
    && Number.isFinite(rightSpan)
    && Math.abs(leftSpan - rightSpan) > 1e-6;
};

const gutterBarsForContentRange = (range, extent, barIntervalSeconds) => {
  const interval = Math.max(Number(barIntervalSeconds) || 0, 1);
  const contentSpan = (Number(range?.to) - Number(range?.from)) / interval;
  const domain = sessionDomainSeconds(Number(extent?.last) * 1000);
  const sessionSpan = (domain.last - domain.first) / interval;
  return Math.min(
    RIGHT_GUTTER_BARS,
    rightGutterBarsForContentSpan(contentSpan, sessionSpan)
  );
};

const sessionOpenZoomViolation = (
  range,
  accepted,
  sessionOpen,
  barIntervalSeconds,
  zoomIntent
) => {
  if (zoomIntent !== true || !Number.isFinite(sessionOpen)) return null;
  const from = Number(range?.from);
  const to = Number(range?.to);
  if (![from, to].every(Number.isFinite)) return null;

  const tolerance = Math.max(Number(barIntervalSeconds) || 0, 1);
  if (from < sessionOpen - tolerance) return 'crossed';

  const acceptedFrom = Number(accepted?.from);
  const acceptedTo = Number(accepted?.to);
  if (![acceptedFrom, acceptedTo].every(Number.isFinite)) return null;

  const acceptedAtOpen = acceptedFrom <= sessionOpen + tolerance;
  const candidateAtOpen = from <= sessionOpen + tolerance;
  const acceptedSpan = acceptedTo - acceptedFrom;
  const candidateSpan = to - from;
  if (!acceptedAtOpen && candidateAtOpen && candidateSpan > acceptedSpan + tolerance) {
    return 'crossed';
  }
  if (acceptedAtOpen && candidateAtOpen && to < acceptedTo - tolerance) {
    return 'collapsed';
  }
  return null;
};

const sessionOpenPanClipViolation = (
  range,
  accepted,
  sessionOpen,
  barIntervalSeconds,
  panIntent
) => {
  if (!panIntent || !Number.isFinite(sessionOpen)) return false;
  const from = Number(range?.from);
  const to = Number(range?.to);
  const acceptedFrom = Number(accepted?.from);
  const acceptedTo = Number(accepted?.to);
  if (![from, to, acceptedFrom, acceptedTo].every(Number.isFinite)) return false;

  const tolerance = Math.max(Number(barIntervalSeconds) || 0, 1);
  const candidateAtOpen = from <= sessionOpen + tolerance;
  const candidateSpan = to - from;
  const acceptedSpan = acceptedTo - acceptedFrom;
  return candidateAtOpen && candidateSpan < acceptedSpan - tolerance;
};

const semanticCorrectedZoomRange = (range, extent, barIntervalSeconds) => {
  const interval = Math.max(Number(barIntervalSeconds) || 0, 1);
  const domain = sessionDomainSeconds(Number(extent?.last) * 1000);
  const sessionSpan = domain.last - domain.first;
  const visibleSpan = Number(range?.to) - Number(range?.from);
  if (!(visibleSpan > 0) || !(sessionSpan > 0)) return range;

  const gutter = rightGutterBarsForVisibleSpan(
    visibleSpan / interval,
    sessionSpan / interval
  ) * interval;
  const tolerance = edgeToleranceSeconds(interval);
  let to = Number(range.to);
  if (Math.abs(to - (domain.last + gutter)) <= tolerance) {
    to = domain.last;
  } else if (Math.abs(to - (Number(extent.last) + gutter)) <= tolerance) {
    to = Number(extent.last);
  }
  return { from: Number(range.from), to };
};

const AdvancedFlowsDashboard = ({ memberCredentials, isActive = true }) => {
  const chartContainerRef = useRef(null);
  const tvWidgetRef = useRef(null);
  const dataFeedRef = useRef(null);
  const readyPRef = useRef(null);
  const studyDisposersRef = useRef(new Set());
  const rightEdgeTimersRef = useRef(new Set());
  const gestureCorrectionTimerRef = useRef(null);
  const gestureActiveRef = useRef(false);
  const pendingLiveMinuteReanchorRef = useRef(false);
  const pendingLiveMinutePreviousBarRef = useRef(null);
  const liveMinuteSettleTimerRef = useRef(null);
  const liveMinuteSettleChartRef = useRef(null);
  const liveMinuteReanchorRef = useRef(null);

  const prevDynamicLevelsRef = useRef(null);
  const prevTopOptionsRef = useRef(null);
  const programmaticRangeUntilRef = useRef(0);
  const userIntentUntilRef = useRef(0);
  const userIntentGenerationRef = useRef(0);
  const zoomIntentUntilRef = useRef(0);
  const panIntentUntilRef = useRef(0);
  const pointerGestureTypeRef = useRef(null);
  const pointerIntentUntilRef = useRef(0);
  const sharedWindowRef = useRef(null);
  const applySharedWindowRef = useRef(null);
  const windowVerifyTimersRef = useRef(new Set());
  const isActiveRef = useRef(isActive);
  const focusedMinuteShapeRef = useRef(null);
  const indexBarsRef = useRef([]);
  const syncFocusedMinuteShapeRef = useRef(() => {});
  const pendingHistoryVersionRef = useRef(null);
  const pendingHistoryAnchorRef = useRef(null);
  const chartStudyInputsRef = useRef(null);
  const greekStudyAvailabilityRef = useRef({ totalGex: false, gts: false });
  // The range this component last wrote. A reported range matching it is an echo
  // of our own write, however late the library delivers the event.
  const appliedRangeRef = useRef(null);
  // The last valid range observed on screen. The gesture wall restores this
  // range, including valid movement smaller than the programmatic echo tolerance.
  const acceptedRangeRef = useRef(null);
  const timeViewDetachedRef = useRef(false);
  const acceptedRangeRevisionRef = useRef(0);
  // The replay cutoff the repaint effect last acted on, and whether a cutoff
  // change was skipped because a history repaint owned that frame.
  const appliedReplayCutoffRef = useRef(null);
  const deferredReplayCutoffRef = useRef(false);

  const [isDataReady, setIsDataReady] = useState(true);
  const [chartInitError, setChartInitError] = useState(null);
  const [chartInitAttempt, setChartInitAttempt] = useState(0);
  const [replayCutoffRetry, setReplayCutoffRetry] = useState(0);
  const [chartLibraryReady, setChartLibraryReady] = useState(
    () => Boolean(window.TradingView)
  );
  const [timeViewDetached, setTimeViewDetached] = useState(false);
  const updateTimeViewDetached = useCallback((detached) => {
    timeViewDetachedRef.current = detached;
    setTimeViewDetached(detached);
  }, []);

  const memberId = memberCredentials?.memberId;
  const isMobile = isMobileChart();

  // Consuming a history version only moves a ref, so the cutoff effect that
  // stood down for that repaint has nothing in its deps to re-run it. The
  // repaint calls this once it settles, so a scrub it swallowed still lands.
  const flushDeferredReplayCutoff = useCallback(() => {
    if (!deferredReplayCutoffRef.current) return;
    deferredReplayCutoffRef.current = false;
    setReplayCutoffRetry((value) => value + 1);
  }, []);

  const getIndexData = useWebSocketDataStore((state) => state.getIndexData);
  const indexData = useWebSocketDataStore((state) => state.indexData);
  const indexHistoryVersion = useWebSocketDataStore((state) => state.indexHistoryVersion);
  const seenHistoryVersionRef = useRef(indexHistoryVersion);
  const seenHistoryChartRef = useRef(null);
  const latestHistoryVersionRef = useRef(indexHistoryVersion);
  latestHistoryVersionRef.current = indexHistoryVersion;

  const indexBars = useMemo(() => indexData?.SPX ?? [], [indexData?.SPX]);
  const lastBar = indexBars[indexBars.length - 1];
  // Index bars carry epoch milliseconds: the store buckets them with
  // `timestamp % 60000` and formats them via `new Date(barTime)`.
  const lastBarMs = lastBar ? (lastBar.time ?? lastBar.t) : null;
  const previousLastBarMsRef = useRef(lastBarMs);
  const dataState = useDataState({
    hasData: indexBars.length > 0 && chartLibraryReady && !chartInitError,
    lastUpdated: lastBarMs,
    error: chartInitError,
  });

  const sharedWindow = useChartTimeWindowStore((state) => state.preference);
  const sharedWindowSource = useChartTimeWindowStore((state) => state.source);
  const publishUserRange = useChartTimeWindowStore((state) => state.publishUserRange);
  const sharedPriceWindow = useChartPriceWindowStore((state) => state.preference);
  const sharedPriceWindowSource = useChartPriceWindowStore((state) => state.source);
  const publishPriceUserRange = useChartPriceWindowStore((state) => state.publishUserRange);
  const resetPriceWindow = useChartPriceWindowStore((state) => state.reset);
  const replaying = usePlaybackStore((state) => state.mode === 'playing');
  const replayCutoffMs = usePlaybackStore((state) => state.replayCutoffMs);
  const focusedMinuteMs = useWidgetMinuteFocusStore((state) => state.minuteMs);
  const focusSourceId = useWidgetMinuteFocusStore((state) => state.sourceId);
  useFocusedMinuteSourceCleanup('chart');

  const focusedMinuteAvailable = useMemo(
    () => focusedMinuteMs != null && indexBars.some(
      (item) => minuteBucketMs(item?.time ?? item?.t) === focusedMinuteMs,
    ),
    [focusedMinuteMs, indexBars],
  );

  const removeFocusedMinuteShape = useCallback(() => {
    const shape = focusedMinuteShapeRef.current;
    if (!shape) return;
    try { shape.chart.removeEntity?.(shape.id); } catch { /* non-fatal */ }
    focusedMinuteShapeRef.current = null;
  }, []);

  const syncFocusedMinuteShape = useCallback((chart) => {
    const focus = useWidgetMinuteFocusStore.getState();
    const minuteMs = focus.minuteMs;
    const time = minuteMs == null ? null : minuteMs / 1000;
    const barAvailable = minuteMs != null && indexBarsRef.current.some(
      (item) => minuteBucketMs(item?.time ?? item?.t) === minuteMs,
    );
    let visible = null;
    try { visible = chart?.getVisibleRange?.(); } catch { /* non-fatal API drift */ }

    const shouldShow = isActiveRef.current
      && focus.sourceId !== 'chart'
      && barAvailable
      && visible
      && time >= Number(visible.from)
      && time <= Number(visible.to);
    const current = focusedMinuteShapeRef.current;

    if (!shouldShow) {
      removeFocusedMinuteShape();
      return;
    }
    if (current?.chart === chart && current.minuteMs === minuteMs) return;

    removeFocusedMinuteShape();
    try {
      const id = chart.createShape?.(
        { time },
        {
          shape: 'vertical_line',
          lock: true,
          disableSave: true,
          disableSelection: true,
          disableUndo: true,
          overrides: {
            linecolor: 'rgba(234,239,247,0.75)',
            linewidth: 1,
            linestyle: 2,
            showLabel: false,
          },
        },
      );
      if (id != null) focusedMinuteShapeRef.current = { chart, id, minuteMs };
    } catch { /* non-fatal API drift */ }
  }, [removeFocusedMinuteShape]);

  useLayoutEffect(() => {
    indexBarsRef.current = indexBars;
    syncFocusedMinuteShapeRef.current = syncFocusedMinuteShape;
  }, [indexBars, syncFocusedMinuteShape]);

  useEffect(() => {
    isActiveRef.current = isActive;
    if (!isActive) useWidgetMinuteFocusStore.getState().clearFocusedMinute('chart');
  }, [isActive]);

  useEffect(() => {
    let cancelled = false;
    readyPRef.current?.then((chart) => {
      if (!chart || cancelled) return;
      syncFocusedMinuteShape(chart);
    });
    return () => { cancelled = true; };
  }, [focusSourceId, focusedMinuteMs, focusedMinuteAvailable, isActive, syncFocusedMinuteShape]);

  const markProgrammaticRange = useCallback((ms = PROGRAMMATIC_RANGE_MS) => {
    programmaticRangeUntilRef.current = Math.max(
      programmaticRangeUntilRef.current,
      performance.now() + ms
    );
  }, []);

  const isProgrammaticChange = useCallback(
    () => performance.now() <= programmaticRangeUntilRef.current,
    []
  );

  const gestureZoomIntent = useCallback(() => {
    const now = performance.now();
    if (now <= panIntentUntilRef.current) return false;
    if (now <= zoomIntentUntilRef.current) return true;
    return null;
  }, []);

  const setAcceptedRange = useCallback((range, { preserveRevision = false } = {}) => {
    acceptedRangeRef.current = range;
    if (!preserveRevision) acceptedRangeRevisionRef.current += 1;
  }, []);

  // Records the range currently on screen after any write this component made,
  // including resets whose resulting range we cannot predict.
  const noteAppliedRange = useCallback((chart) => {
    try {
      const range = chart.getVisibleRange();
      const from = Number(range?.from);
      const to = Number(range?.to);
      const visible = Number.isFinite(from) && Number.isFinite(to)
        ? { from, to }
        : null;
      appliedRangeRef.current = visible;
      setAcceptedRange(visible);
    } catch {
      appliedRangeRef.current = null;
      setAcceptedRange(null);
    }
  }, [setAcceptedRange]);

  // Read bar times from the store rather than the chart's visible range, which
  // the saved-view restore can transiently corrupt. Bars carry `time` in ms.
  // Reads through the store getter, never a captured `indexData` snapshot: this
  // has to stay referentially stable or the effects below would tear the widget
  // down on every websocket tick.
  const barTimesSec = useCallback(() => {
    const bars = typeof getIndexData === 'function'
      ? getIndexData('SPX')
      : useWebSocketDataStore.getState().indexData?.SPX;
    if (!Array.isArray(bars)) return [];
    const times = [];
    for (const bar of bars) {
      const ms = Number(bar?.time);
      if (Number.isFinite(ms)) times.push(Math.floor(ms / 1000));
    }
    return times;
  }, [getIndexData]);

  // Keyed on the bars array identity so a pan does not rebuild and re-measure
  // the whole series on every frame.
  const windowContextCacheRef = useRef({ source: null, value: null });

  const windowContext = useCallback(() => {
    const bars = typeof getIndexData === 'function'
      ? getIndexData('SPX')
      : useWebSocketDataStore.getState().indexData?.SPX;
    const cache = windowContextCacheRef.current;
    if (cache.source === bars) return cache.value;

    const times = barTimesSec();
    const value = {
      extent: clampExtentToSessionClose(extentFromEpochSeconds(times)),
      barIntervalSeconds: inferBarIntervalSeconds(times),
    };
    windowContextCacheRef.current = { source: bars, value };
    return value;
  }, [barTimesSec, getIndexData]);

  // The ceiling a settled gesture may not exceed. Shared with the range wall so
  // the right-offset and timestamp APIs enforce the same candidate-span rule.
  const rightOffsetCeiling = useCallback((range) => {
    const { extent, barIntervalSeconds } = windowContext();
    const zoomIntent = gestureZoomIntent();
    const anchorMode = zoomIntent === true
      ? WINDOW_MODE.LATEST
      : zoomIntent === false
        ? WINDOW_MODE.SESSION
        : sharedWindowRef.current?.mode;
    const maxTo = interactiveRightEdgeSeconds(
      range,
      extent,
      barIntervalSeconds,
      anchorMode
    );
    if (!extent || !Number.isFinite(maxTo)) return RIGHT_GUTTER_BARS;
    return Math.max(0, (maxTo - extent.last) / barIntervalSeconds);
  }, [gestureZoomIntent, windowContext]);

  /** Restore the offset implied by an anchored range after a range write. */
  const ensureRightOffset = useCallback((chart, rightOffsetBars) => {
    if (!chart) return;
    try {
      const timeScale = chart.getTimeScale();
      const current = timeScale.rightOffset();
      if (Number.isFinite(current) && Math.abs(current - rightOffsetBars) <= 1e-6) return;
      markProgrammaticRange();
      timeScale.setRightOffset(rightOffsetBars);
    } catch { /* API drift between library builds - non-fatal */ }
  }, [markProgrammaticRange]);

  const restoreAcceptedRange = useCallback((chart, { onSettled } = {}) => {
    const accepted = acceptedRangeRef.current;
    const from = Number(accepted?.from);
    const to = Number(accepted?.to);
    if (!chart || !Number.isFinite(from) || !Number.isFinite(to)) return false;
    const target = { from, to };

    const { extent, barIntervalSeconds } = windowContext();
    const anchored = extent
      && edgeTakesGutter(target.to, extent.last, barIntervalSeconds);
    const rightOffsetBars = anchored
      ? Math.max(0, (target.to - extent.last) / barIntervalSeconds)
      : 0;
    const previousApplied = appliedRangeRef.current;
    const previousAccepted = acceptedRangeRef.current;
    markProgrammaticRange();
    let settled;
    try {
      settled = Promise.resolve(chart.setVisibleRange(target));
    } catch {
      return false;
    }
    appliedRangeRef.current = target;
    setAcceptedRange(target, { preserveRevision: true });
    settled.then(
      () => {
        const applied = appliedRangeRef.current === target;
        if (applied && anchored) ensureRightOffset(chart, rightOffsetBars);
        onSettled?.(applied);
      },
      () => {
        if (appliedRangeRef.current === target) appliedRangeRef.current = previousApplied;
        if (acceptedRangeRef.current === target) {
          setAcceptedRange(previousAccepted, { preserveRevision: true });
        }
        onSettled?.(false);
      }
    );
    return true;
  }, [ensureRightOffset, markProgrammaticRange, setAcceptedRange, windowContext]);

  /**
   * Apply the shared preference to this chart. Returns false when nothing
   * applicable is stored, which is every caller's signal to fall back to its own
   * right-edge behaviour.
   */
  const applySharedWindow = useCallback((
    chart,
    {
      force = false,
      attempt = 0,
      onSettled,
      preserveAcceptedRevision = false,
    } = {}
  ) => {
    if (!chart) return false;
    const { extent, barIntervalSeconds } = windowContext();
    if (!extent) return false;

    const { status, range } = resolveWindowPreference(sharedWindowRef.current, extent, {
      barIntervalSeconds,
    });
    if (status === RESOLUTION_STATUS.UNAVAILABLE || !range) return false;

    // Blank room past the resolved edge so a marker on the final bar draws
    // whole, at an anchored edge only -- see `edgeTakesGutter`. Padding an
    // interior range changes the interval that was requested. Never stored and
    // never published either way: the gutter is padding, not user intent.
    const gutterBars = edgeTakesGutter(range.to, extent.last, barIntervalSeconds)
      ? gutterBarsForContentRange(range, extent, barIntervalSeconds)
      : 0;
    const gutter = gutterBars * barIntervalSeconds;
    const target = { from: range.from, to: range.to + gutter };
    const targetRightOffsetBars = Math.max(
      0,
      (target.to - extent.last) / barIntervalSeconds
    );
    const userIntentGeneration = userIntentGenerationRef.current;

    // This chart stays mounted behind the Bookmap tabs, so a redundant write
    // here is an asynchronous range change pushed into a hidden iframe for no
    // visible effect. Skip it when the range is already applied.
    const accepted = acceptedRangeRef.current;
    if (!force && accepted && accepted.from === target.from && accepted.to === target.to) {
      if (targetRightOffsetBars > 0) ensureRightOffset(chart, targetRightOffsetBars);
      onSettled?.(true);
      return true;
    }

    markProgrammaticRange();
    let settled;
    try {
      // Returns Promise<void>; a rejection is non-fatal but must not surface as
      // an unhandled rejection.
      settled = Promise.resolve(chart.setVisibleRange(target));
    } catch {
      return false;
    }

    // Remember the intended range, not a read-back: a clamped partial
    // application must never be republished as the user's intent, and the spec
    // requires the stored preference to survive it intact.
    const previousAccepted = acceptedRangeRef.current;
    appliedRangeRef.current = target;
    setAcceptedRange(target, { preserveRevision: preserveAcceptedRevision });

    settled.then(
      () => {
        // `setVisibleRange` recomputes the right offset, so an anchored range
        // has to re-assert its gutter once the write settles. An interior range
        // must not: its offset is legitimately far below the gutter, since the
        // last bar is off-screen to the right, and `setRightOffset` would scroll
        // back toward the latest bar and undo the interval just applied.
        //
        // Identity, not equality: the ref marks whichever invocation wrote last.
        // These completions are not ordered, so an older anchored application
        // could otherwise reassert its gutter over a newer interior one and beat
        // the last user action.
        const applied = appliedRangeRef.current === target
          && userIntentGenerationRef.current === userIntentGeneration;
        if (
          targetRightOffsetBars > 0
          && applied
        ) {
          ensureRightOffset(chart, targetRightOffsetBars);
        }
        onSettled?.(applied);
      },
      () => {
        // The write never landed, so the echo record is a lie. Left in place it
        // would match every later application by value, and each one would
        // report success and skip reapplying -- one rejection would suppress the
        // shared window on this chart for the rest of the session.
        if (appliedRangeRef.current === target) appliedRangeRef.current = null;
        if (acceptedRangeRef.current === target) {
          setAcceptedRange(previousAccepted, {
            preserveRevision: preserveAcceptedRevision,
          });
        }
        onSettled?.(false);
      }
    );

    if (attempt + 1 < WINDOW_APPLY_ATTEMPTS) {
      const verifyTimer = setTimeout(() => {
        windowVerifyTimersRef.current.delete(verifyTimer);
        if (
          appliedRangeRef.current !== target
          || userIntentGenerationRef.current !== userIntentGeneration
        ) return;

        if (targetRightOffsetBars > 0) ensureRightOffset(chart, targetRightOffsetBars);
        let actual = null;
        try { actual = chart.getVisibleRange(); } catch { /* retry below */ }
        const actualFrom = Number(actual?.from);
        const actualSpan = Number(actual?.to) - Number(actual?.from);
        const leftEdgeLanded = Number.isFinite(actualFrom)
          && Math.abs(actualFrom - target.from) <= barIntervalSeconds;
        if (
          leftEdgeLanded
          && actualSpan >= (target.to - target.from) - barIntervalSeconds
        ) return;

        // The optimistic record is a lie once the write is known to have been
        // dropped. Restore the prior accepted range before the forced retry so
        // a rejected retry cannot discard the last finite recovery target.
        appliedRangeRef.current = null;
        setAcceptedRange(previousAccepted, {
          preserveRevision: preserveAcceptedRevision,
        });
        applySharedWindowRef.current?.(chart, {
          force: true,
          attempt: attempt + 1,
          onSettled,
          preserveAcceptedRevision,
        });
      }, WINDOW_APPLY_RETRY_MS * (attempt + 1));
      windowVerifyTimersRef.current.add(verifyTimer);
    }
    return true;
  }, [ensureRightOffset, markProgrammaticRange, setAcceptedRange, windowContext]);

  applySharedWindowRef.current = applySharedWindow;

  const applyDefaultLatestWindow = useCallback((
    chart,
    { onSettled, preserveAcceptedRevision = false } = {}
  ) => {
    if (!chart) return false;
    const { extent, barIntervalSeconds } = windowContext();
    if (!extent) return false;
    const range = {
      from: Math.max(extent.first, extent.last - DEFAULT_VISIBLE_SECONDS),
      to: extent.last,
    };
    if (!(range.to > range.from)) return false;
    const gutterBars = gutterBarsForContentRange(range, extent, barIntervalSeconds);
    const target = {
      from: range.from,
      to: range.to + gutterBars * barIntervalSeconds,
    };

    markProgrammaticRange();
    const previousAccepted = acceptedRangeRef.current;
    try {
      const timeScale = chart.getTimeScale();
      timeScale.usePercentageRightOffset().setValue(false);
      timeScale.defaultRightOffset().setValue(gutterBars);
      timeScale.setRightOffset(gutterBars);
      Promise.resolve(chart.setVisibleRange(target)).then(
        () => {
          const applied = appliedRangeRef.current === target;
          if (applied) ensureRightOffset(chart, gutterBars);
          onSettled?.(applied);
        },
        () => {
          if (appliedRangeRef.current === target) appliedRangeRef.current = null;
          if (acceptedRangeRef.current === target) {
            setAcceptedRange(previousAccepted, {
              preserveRevision: preserveAcceptedRevision,
            });
          }
          onSettled?.(false);
        }
      );
      appliedRangeRef.current = target;
      setAcceptedRange(target, { preserveRevision: preserveAcceptedRevision });
      return true;
    } catch {
      return false;
    }
  }, [ensureRightOffset, markProgrammaticRange, setAcceptedRange, windowContext]);

  // `onSettled(applied)` reports that the repaint is over, not that it
  // succeeded. Every terminal path has to reach it -- a rejected write, and a
  // range write that never starts for want of an extent or a finite accepted
  // range -- or a replay cutoff this repaint deferred waits on a retry that is
  // never scheduled. `applied` is false on those paths, so a caller can still
  // hold state that only a landed range may retire.
  const restoreHistoryRange = useCallback((chart, previousLastBarMs, onSettled) => {
    const accepted = acceptedRangeRef.current;
    const hasPreviousLastBar = previousLastBarMs != null
      && Number.isFinite(Number(previousLastBarMs));
    const previousLastBarSeconds = hasPreviousLastBar
      ? Number(previousLastBarMs) / 1000
      : null;
    const { barIntervalSeconds } = windowContext();
    const preference = useChartTimeWindowStore.getState().preference;
    const followsDataEdge = !preference
      || preference.mode === WINDOW_MODE.LATEST
      || preference.mode === WINDOW_MODE.SESSION;
    const wasAnchoredToPreviousTail = hasPreviousLastBar
      && edgeTakesGutter(accepted?.to, previousLastBarSeconds, barIntervalSeconds);

    let started;
    if (followsDataEdge && wasAnchoredToPreviousTail) {
      sharedWindowRef.current = preference;
      started = applySharedWindow(chart, {
        force: true,
        onSettled,
        preserveAcceptedRevision: true,
      }) || applyDefaultLatestWindow(chart, {
        onSettled,
        preserveAcceptedRevision: true,
      });
    } else {
      started = restoreAcceptedRange(chart, { onSettled });
    }
    if (!started) onSettled?.(false);
    return started;
  }, [applyDefaultLatestWindow, applySharedWindow, restoreAcceptedRange, windowContext]);

  const reanchorLiveMinute = useCallback((chart, previousLastBarMs) => {
    const windowState = useChartTimeWindowStore.getState();
    sharedWindowRef.current = windowState.preference;
    if (timeViewDetachedRef.current) return false;

    if (windowState.source === WINDOW_SOURCE_ID) {
      const previousLastBarSeconds = Number(previousLastBarMs) / 1000;
      const { barIntervalSeconds } = windowContext();
      let visible = null;
      try { visible = chart?.getVisibleRange?.(); } catch { /* chart rebuild */ }
      if (
        !Number.isFinite(previousLastBarSeconds)
        || !edgeTakesGutter(visible?.to, previousLastBarSeconds, barIntervalSeconds)
      ) return false;
    }

    return applySharedWindow(chart, { force: true });
  }, [applySharedWindow, windowContext]);

  liveMinuteReanchorRef.current = reanchorLiveMinute;

  const clearPendingLiveMinuteReanchor = useCallback(() => {
    if (liveMinuteSettleTimerRef.current) {
      clearTimeout(liveMinuteSettleTimerRef.current);
    }
    liveMinuteSettleTimerRef.current = null;
    liveMinuteSettleChartRef.current = null;
    pendingLiveMinuteReanchorRef.current = false;
    pendingLiveMinutePreviousBarRef.current = null;
  }, []);

  const schedulePendingLiveMinuteReanchor = useCallback((chart) => {
    if (liveMinuteSettleTimerRef.current) {
      clearTimeout(liveMinuteSettleTimerRef.current);
    }
    liveMinuteSettleChartRef.current = chart;
    const settle = () => {
      if (liveMinuteSettleChartRef.current !== chart) return;
      liveMinuteSettleTimerRef.current = null;
      liveMinuteSettleChartRef.current = null;
      if (!pendingLiveMinuteReanchorRef.current) return;
      if (gestureActiveRef.current || publishRafRef.current) return;
      const previousLastBarMs = pendingLiveMinutePreviousBarRef.current;
      pendingLiveMinuteReanchorRef.current = false;
      pendingLiveMinutePreviousBarRef.current = null;
      liveMinuteReanchorRef.current?.(chart, previousLastBarMs);
    };
    const delay = Math.max(0, userIntentUntilRef.current - performance.now() + 1);
    liveMinuteSettleTimerRef.current = setTimeout(settle, delay);
  }, []);

  const applyTimeWindowMode = useCallback((mode) => {
    if (mode !== WINDOW_MODE.SESSION && mode !== WINDOW_MODE.LATEST) return false;
    const { extent, barIntervalSeconds } = windowContext();
    if (!extent) return false;

    const sessionDomain = mode === WINDOW_MODE.SESSION
      ? sessionDomainSeconds(extent.last * 1000)
      : null;
    const range = sessionDomain
      ? { from: sessionDomain.first, to: sessionDomain.last }
      : {
        from: Math.max(extent.first, extent.last - DEFAULT_VISIBLE_SECONDS),
        to: extent.last,
      };
    if (!(range.to > range.from)) return false;

    // An explicit selection supersedes any gesture still settling. After the
    // programmatic guard expires, stale gesture work must not publish over it.
    userIntentUntilRef.current = 0;
    panIntentUntilRef.current = 0;
    zoomIntentUntilRef.current = 0;
    updateTimeViewDetached(false);
    if (gestureCorrectionTimerRef.current) {
      clearTimeout(gestureCorrectionTimerRef.current);
      gestureCorrectionTimerRef.current = null;
    }

    const publishedRange = mode === WINDOW_MODE.LATEST
      ? { from: extent.last - DEFAULT_VISIBLE_SECONDS, to: extent.last }
      : range;
    publishUserRange(WINDOW_SOURCE_ID, publishedRange, extent, {
      barIntervalSeconds,
      isDefaultSpan: mode === WINDOW_MODE.LATEST ? true : undefined,
      tradingDate: etDateKey(extent.last * 1000),
    });
    sharedWindowRef.current = useChartTimeWindowStore.getState().preference;
    readyPRef.current?.then((chart) => {
      if (chart) applySharedWindow(chart);
    });
    return true;
  }, [applySharedWindow, publishUserRange, updateTimeViewDetached, windowContext]);

  // The declarative `scroll_past_end: false` and the Lightweight-Charts-shaped
  // `timeScale` block in chartConfig are both ignored by this bundled library,
  // so the invariant has to be enforced against the live time-scale API.
  const clampRightOffset = useCallback((chart) => {
    if (!chart) return;
    try {
      const timeScale = chart.getTimeScale();
      // charting_library.d.ts: `rightOffset(): number`.
      const current = timeScale.rightOffset();
      const range = chart.getVisibleRange();
      const accepted = acceptedRangeRef.current;
      const from = Number(range?.from);
      const to = Number(range?.to);
      if (sameVisibleRange({ from, to }, accepted)) return;
      const ceiling = rightOffsetCeiling(range);
      if (!Number.isFinite(current) || current <= ceiling) return;
      markProgrammaticRange();
      timeScale.setRightOffset(ceiling);
    } catch { /* API drift between library builds - non-fatal */ }
  }, [markProgrammaticRange, rightOffsetCeiling]);

  const publishVisibleRange = useCallback((chart) => {
    if (!chart) return;
    // Publishing requires a positive user signal. Without this the activation
    // reset and the asynchronous saved-view restore both published themselves as
    // user intent and overwrote whatever the Bookmap tabs had selected.
    const userIntentActive = performance.now() <= userIntentUntilRef.current;
    if (!userIntentActive) return;

    const { extent, barIntervalSeconds } = windowContext();
    if (!extent) return;
    try {
      const range = chart.getVisibleRange();
      const from = Number(range?.from);
      const to = Number(range?.to);
      if (!Number.isFinite(from) || !Number.isFinite(to)) return;
      const accepted = acceptedRangeRef.current;
      const recordedZoomIntent = gestureZoomIntent();
      const zoomIntent = recordedZoomIntent === true
        || (recordedZoomIntent == null && visibleRangeSpanChanged({ from, to }, accepted));
      const panIntent = recordedZoomIntent === false
        || (recordedZoomIntent == null && !zoomIntent);
      const domain = sessionDomainSeconds(extent.last * 1000);
      const openViolation = sessionOpenZoomViolation(
        { from, to },
        accepted,
        domain.first,
        barIntervalSeconds,
        zoomIntent
      );
      const anchorTolerance = edgeToleranceSeconds(barIntervalSeconds);
      const leftEdgeViolation = from < domain.first - anchorTolerance;
      const openPanClipViolation = sessionOpenPanClipViolation(
        { from, to },
        accepted,
        domain.first,
        barIntervalSeconds,
        panIntent
      );
      if (openViolation || leftEdgeViolation || openPanClipViolation) return;

      // A later offset callback can report the accepted zoom after the publish
      // callback stored it. Treat that identical range as stable before the
      // pan ceiling classifies its unchanged span.
      const applied = appliedRangeRef.current;
      let appliedEcho = false;
      if (applied) {
        const tolerance = Math.max(barIntervalSeconds, 1) * 2;
        appliedEcho = Math.abs(from - applied.from) <= tolerance
          && Math.abs(to - applied.to) <= tolerance;
      }

      // Bound the range itself rather than trusting the right-offset clamp.
      // Repeated drags were leaving the right edge many hours past the close --
      // observed running into the following morning -- which means the offset
      // subscription is not catching every overscroll. Correcting the range is
      // independent of how the library reports offsets.
      // The same ceiling the drag clamp uses, expressed as a timestamp: keyed to
      // the loaded data's date, not today's, or prior-session bars would be
      // allowed to scroll to a close days away.
      const maxTo = interactiveRightEdgeSeconds(
        { from, to },
        extent,
        barIntervalSeconds,
        zoomIntent === true
          ? WINDOW_MODE.LATEST
          : panIntent
            ? WINDOW_MODE.SESSION
            : sharedWindowRef.current?.mode
      );
      if (!Number.isFinite(maxTo)) return;
      const rightEdgeViolation = zoomIntent === true
        ? Math.abs(to - maxTo) > anchorTolerance
        : to > maxTo + anchorTolerance;
      if (rightEdgeViolation) return;

      setAcceptedRange({ from, to }, { preserveRevision: appliedEcho });
      if (appliedEcho) return;
      if (zoomIntent === true) {
        updateTimeViewDetached(false);
        publishUserRange(WINDOW_SOURCE_ID, { from, to }, extent, {
          barIntervalSeconds,
          tradingDate: etDateKey(extent.last * 1000),
        });
      } else if (panIntent) {
        // A pan publishes the position it lands on so the Flowmap and both GEX
        // widgets follow it there. `positionIsIntent` stops the classifier
        // reading the interval as a duration and re-anchoring it to each
        // chart's own data edge, which would move them somewhere nobody
        // dragged to.
        //
        // Only when the span held: a pan is a translate by definition, so a
        // pan-classified range whose span moved is classification noise -- a
        // one-finger inspection touch reports exactly that -- and publishing it
        // would push a span nobody asked for onto every other chart.
        if (visibleRangeSpanChanged({ from, to }, accepted)) {
          updateTimeViewDetached(true);
        } else {
          // Keep local ownership only if the store rejects the position.
          const published = publishUserRange(WINDOW_SOURCE_ID, { from, to }, extent, {
            barIntervalSeconds,
            positionIsIntent: true,
            tradingDate: etDateKey(extent.last * 1000),
          });
          updateTimeViewDetached(!published);
        }
      }
      appliedRangeRef.current = { from, to };
    } catch { /* API drift between library builds - non-fatal */ }
  }, [
    gestureZoomIntent,
    publishUserRange,
    setAcceptedRange,
    updateTimeViewDetached,
    windowContext,
  ]);

  const correctVisibleRange = useCallback((chart) => {
    if (!chart) return false;
    const userIntentActive = performance.now() <= userIntentUntilRef.current;
    if (isProgrammaticChange() && !userIntentActive) return false;
    const { extent, barIntervalSeconds } = windowContext();
    if (!extent) return false;
    try {
      const range = chart.getVisibleRange();
      const from = Number(range?.from);
      const to = Number(range?.to);
      const accepted = acceptedRangeRef.current;
      if (sameVisibleRange({ from, to }, accepted)) return false;
      const recordedZoomIntent = gestureZoomIntent();
      const spanChanged = visibleRangeSpanChanged({ from, to }, accepted);
      const zoomIntent = recordedZoomIntent === true
        || (recordedZoomIntent == null && userIntentActive && spanChanged);
      const panIntent = recordedZoomIntent === false
        || (recordedZoomIntent == null && userIntentActive && !spanChanged);
      const domain = sessionDomainSeconds(extent.last * 1000);
      const openViolation = sessionOpenZoomViolation(
        { from, to },
        accepted,
        domain.first,
        barIntervalSeconds,
        zoomIntent
      );
      const maxTo = interactiveRightEdgeSeconds(
        { from, to },
        extent,
        barIntervalSeconds,
        zoomIntent === true
          ? WINDOW_MODE.LATEST
          : panIntent
            ? WINDOW_MODE.SESSION
            : sharedWindowRef.current?.mode
      );
      if (!Number.isFinite(maxTo)) return false;
      const anchorTolerance = edgeToleranceSeconds(barIntervalSeconds);
      const leftEdgeViolation = from < domain.first - anchorTolerance;
      const openPanClipViolation = sessionOpenPanClipViolation(
        { from, to },
        accepted,
        domain.first,
        barIntervalSeconds,
        panIntent
      );
      const rightEdgeViolation = zoomIntent === true
        ? Math.abs(to - maxTo) > anchorTolerance
        : to > maxTo + anchorTolerance;
      if (
        !openViolation
        && !leftEdgeViolation
        && !openPanClipViolation
        && !rightEdgeViolation
      ) return false;

      const candidateSpan = to - from;
      if (!(candidateSpan > 0)) return false;
      let target;
      if (panIntent) {
        target = { from, to };
        if (leftEdgeViolation || openPanClipViolation) {
          const acceptedSpan = Number(accepted?.to) - Number(accepted?.from);
          const correctedSpan = !leftEdgeViolation
            && Number.isFinite(acceptedSpan)
            && acceptedSpan > 0
            ? acceptedSpan
            : to - from;
          target = {
            from: domain.first,
            to: domain.first + correctedSpan,
          };
        }
        if (target.to > maxTo + anchorTolerance) {
          target = {
            from: maxTo - (target.to - target.from),
            to: maxTo,
          };
        }
      } else {
        target = openViolation === 'collapsed'
          ? accepted
          : openViolation === 'crossed'
            ? { from: domain.first, to: maxTo }
            : zoomIntent === true
              ? { from: maxTo - candidateSpan, to: maxTo }
              : accepted;
      }
      if (!Number.isFinite(target?.from) || !Number.isFinite(target?.to)) return false;
      markProgrammaticRange();
      const previousApplied = appliedRangeRef.current;
      const previousAccepted = acceptedRangeRef.current;
      try {
        appliedRangeRef.current = target;
        setAcceptedRange(target);
        Promise.resolve(chart.setVisibleRange(target)).then(
          () => {
            if (appliedRangeRef.current !== target || acceptedRangeRef.current !== target) return;
            if (zoomIntent) updateTimeViewDetached(false);
            if (zoomIntent === true) {
              const semanticTarget = semanticCorrectedZoomRange(
                target,
                extent,
                barIntervalSeconds
              );
              publishUserRange(WINDOW_SOURCE_ID, semanticTarget, extent, {
                barIntervalSeconds,
                tradingDate: etDateKey(extent.last * 1000),
              });
              return;
            }
            if (!panIntent) return;
            // The corrected pan target, not the raw one: the correction is what
            // keeps the window inside the session, and the other charts have to
            // land on the same interval this one settled at. A correction that
            // had to change the span is not a translate any more, so it stays
            // local like any other span-changed pan.
            if (visibleRangeSpanChanged(target, accepted)) {
              updateTimeViewDetached(true);
              return;
            }
            updateTimeViewDetached(!publishUserRange(WINDOW_SOURCE_ID, target, extent, {
              barIntervalSeconds,
              tradingDate: etDateKey(extent.last * 1000),
              positionIsIntent: true,
            }));
          },
          () => {
            if (appliedRangeRef.current === target) appliedRangeRef.current = previousApplied;
            if (acceptedRangeRef.current === target) {
              setAcceptedRange(previousAccepted);
            }
          }
        );
        return true;
      } catch {
        if (appliedRangeRef.current === target) appliedRangeRef.current = previousApplied;
        if (acceptedRangeRef.current === target) {
          setAcceptedRange(previousAccepted);
        }
        return false;
      }
    } catch {
      return false;
    }
  }, [
    gestureZoomIntent,
    isProgrammaticChange,
    markProgrammaticRange,
    publishUserRange,
    updateTimeViewDetached,
    setAcceptedRange,
    windowContext,
  ]);

  const scheduleRangeCorrection = useCallback((chart, delay = 0) => {
    if (gestureCorrectionTimerRef.current) {
      clearTimeout(gestureCorrectionTimerRef.current);
    }
    gestureCorrectionTimerRef.current = setTimeout(() => {
      gestureCorrectionTimerRef.current = null;
      if (!correctVisibleRange(chart)) clampRightOffset(chart);
      if (pendingLiveMinuteReanchorRef.current) {
        schedulePendingLiveMinuteReanchor(chart);
      }
    }, delay);
  }, [clampRightOffset, correctVisibleRange, schedulePendingLiveMinuteReanchor]);

  // Same reasoning as the Bookmap: collapse a drag's range events to one publish
  // per frame so subscribers are not woken per event.
  const publishRafRef = useRef(null);
  const pricePublishRafRef = useRef(null);
  const pendingPricePublishRef = useRef(null);
  const pendingSharedPriceChartRef = useRef(null);

  const schedulePublish = useCallback((chart) => {
    if (publishRafRef.current) return;
    publishRafRef.current = requestAnimationFrame(() => {
      publishRafRef.current = null;
      publishVisibleRange(chart);
      if (pendingLiveMinuteReanchorRef.current) {
        schedulePendingLiveMinuteReanchor(chart);
      }
    });
  }, [publishVisibleRange, schedulePendingLiveMinuteReanchor]);

  const applySharedPriceWindow = useCallback((chart) => {
    const priceScale = mainPriceScale(chart);
    if (!priceScale) return false;
    const state = useChartPriceWindowStore.getState();
    if (!state.preference) {
      if (state.source == null) return false;
      priceScale.setAutoScale(true);
      return true;
    }
    priceScale.setAutoScale(false);
    priceScale.setVisiblePriceRange(state.preference);
    return true;
  }, []);

  const publishChartPriceWindow = useCallback((chart, capturedRange = null) => {
    const range = capturedRange ?? mainPriceScale(chart)?.getVisiblePriceRange?.();
    const { extent } = windowContext();
    publishPriceUserRange(WINDOW_SOURCE_ID, range, {
      tradingDate: Number.isFinite(extent?.last) ? etDateKey(extent.last * 1000) : null,
    });
  }, [publishPriceUserRange, windowContext]);

  const applySharedPriceWindowWhenIdle = useCallback((chart) => {
    if (gestureActiveRef.current || pricePublishRafRef.current) {
      pendingSharedPriceChartRef.current = chart;
      return false;
    }
    pendingSharedPriceChartRef.current = null;
    return applySharedPriceWindow(chart);
  }, [applySharedPriceWindow]);

  const flushPendingSharedPriceWindow = useCallback(() => {
    const chart = pendingSharedPriceChartRef.current;
    pendingSharedPriceChartRef.current = null;
    if (chart) applySharedPriceWindow(chart);
  }, [applySharedPriceWindow]);

  const capturePendingPricePublish = useCallback((chart) => {
    const pending = pendingPricePublishRef.current;
    if (!pending || pending.chart !== chart || pending.range) return;
    const range = mainPriceScale(chart)?.getVisiblePriceRange?.();
    if (range) pending.range = { from: range.from, to: range.to };
  }, []);

  const resetChartData = useCallback((chart) => {
    capturePendingPricePublish(chart);
    chart.resetData();
  }, [capturePendingPricePublish]);

  const schedulePricePublish = useCallback((chart, releasedRange = null) => {
    const pending = {
      chart,
      range: releasedRange
        ? { from: releasedRange.from, to: releasedRange.to }
        : null,
    };
    pendingPricePublishRef.current = pending;
    if (pricePublishRafRef.current) cancelAnimationFrame(pricePublishRafRef.current);
    pricePublishRafRef.current = requestAnimationFrame(() => {
      capturePendingPricePublish(chart);
      pricePublishRafRef.current = requestAnimationFrame(() => {
        pricePublishRafRef.current = null;
        if (pendingPricePublishRef.current === pending) {
          pendingPricePublishRef.current = null;
        }
        publishChartPriceWindow(chart, pending.range);
        flushPendingSharedPriceWindow();
      });
    });
  }, [capturePendingPricePublish, flushPendingSharedPriceWindow, publishChartPriceWindow]);

  useEffect(() => () => {
    if (publishRafRef.current) cancelAnimationFrame(publishRafRef.current);
    if (pricePublishRafRef.current) cancelAnimationFrame(pricePublishRafRef.current);
    pendingPricePublishRef.current = null;
    pendingSharedPriceChartRef.current = null;
    if (gestureCorrectionTimerRef.current) clearTimeout(gestureCorrectionTimerRef.current);
    clearPendingLiveMinuteReanchor();
    gestureActiveRef.current = false;
    pendingHistoryAnchorRef.current = null;
    for (const timer of windowVerifyTimersRef.current) clearTimeout(timer);
    windowVerifyTimersRef.current.clear();
  }, [clearPendingLiveMinuteReanchor]);

  /**
   * Positive user-gesture signal, mirroring the Bookmap's wheel/drag window.
   *
   * The library renders the chart into its own iframe, so listeners on our
   * container never see these gestures. The iframe is same-origin, so the
   * primary route is its document; `mouse_down`/`mouse_up` are documented widget
   * events and cover the drag case if that document is ever unreachable. Wheel
   * zoom has no widget event, which is why both routes exist.
   */
  const attachGestureListeners = useCallback((widget, chart) => {
    const markUserIntent = () => {
      userIntentGenerationRef.current += 1;
      userIntentUntilRef.current = performance.now() + USER_INTENT_MS;
    };
    const markPanIntent = () => {
      zoomIntentUntilRef.current = 0;
      panIntentUntilRef.current = performance.now() + USER_INTENT_MS * 2;
      markUserIntent();
    };
    const markZoomIntent = () => {
      panIntentUntilRef.current = 0;
      zoomIntentUntilRef.current = performance.now() + USER_INTENT_MS * 2;
      markUserIntent();
    };
    const markUnknownDragIntent = () => {
      panIntentUntilRef.current = 0;
      zoomIntentUntilRef.current = 0;
      markUserIntent();
    };
    let pointerStart = null;
    let priceAutoScaleReleased = false;
    let priceGestureChanged = false;
    let latestActionActive = false;
    let pointerGestureActive = false;
    const releasePriceAutoScale = () => {
      if (priceAutoScaleReleased) return;
      try {
        const priceScale = mainPriceScale(chart);
        if (!priceScale?.isAutoScale?.()) return;
        priceScale.setAutoScale(false);
        priceAutoScaleReleased = true;
      } catch { /* non-fatal TradingView API drift */ }
    };
    const settleDrag = () => {
      if (!gestureActiveRef.current && !pointerGestureActive && !latestActionActive) return;
      const publishPrice = priceGestureChanged;
      const releasedPriceRange = publishPrice
        ? mainPriceScale(chart)?.getVisiblePriceRange?.()
        : null;
      pointerStart = null;
      priceAutoScaleReleased = false;
      priceGestureChanged = false;
      const wasLatestAction = latestActionActive;
      latestActionActive = false;
      pointerGestureActive = false;
      gestureActiveRef.current = false;
      if (wasLatestAction) {
        flushPendingSharedPriceWindow();
        return;
      }
      markUserIntent();
      scheduleRangeCorrection(chart);
      if (publishPrice) schedulePricePublish(chart, releasedPriceRange);
      else flushPendingSharedPriceWindow();
    };
    const settleWheel = (event) => {
      if (event.target?.closest?.('.price-axis')) {
        releasePriceAutoScale();
        schedulePricePublish(chart);
      }
      pointerGestureTypeRef.current = 'zoom';
      markZoomIntent();
      scheduleRangeCorrection(chart, USER_INTENT_MS);
    };
    const disposers = [];

    try {
      const frameDocument = chartContainerRef.current
        ?.querySelector('iframe')?.contentDocument;
      if (frameDocument) {
        let touchZoomActive = false;
        let pinchAwaitingInspection = false;
        const isLatestButton = (target) => Boolean(target?.closest?.(
          '[aria-label="Scroll to the most recent bar"], [title="Scroll to the most recent bar"]'
        ));
        const activateLatestButton = (event) => {
          if (!isLatestButton(event.target)) return;
          if (!applyTimeWindowMode(WINDOW_MODE.LATEST)) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          latestActionActive = false;
          pointerGestureActive = false;
          gestureActiveRef.current = false;
          pointerStart = null;
        };
        const resetPriceAxis = (event) => {
          if (!event.target?.closest?.('.price-axis')) return;
          resetPriceWindow(WINDOW_SOURCE_ID);
        };
        const markPointerIntent = (event) => {
          if (isLatestButton(event.target)) {
            latestActionActive = true;
            pointerGestureActive = true;
            gestureActiveRef.current = true;
            pointerStart = null;
            return;
          }
          latestActionActive = false;
          pointerGestureActive = true;
          gestureActiveRef.current = true;
          pointerIntentUntilRef.current = performance.now() + USER_INTENT_MS * 2;
          pointerGestureTypeRef.current = event.target?.closest?.('.time-axis')
            ? 'zoom'
            : 'pan';
          pointerStart = pointerGestureTypeRef.current === 'pan'
            ? {
                x: event.clientX,
                y: event.clientY,
                pointerType: event.pointerType,
                maxDx: 0,
                maxDy: 0,
              }
            : null;
          if (pointerGestureTypeRef.current === 'zoom') markZoomIntent();
          else markPanIntent();
        };
        const markOnDrag = (event) => {
          if ((event.buttons & 1) !== 1) return;
          if (pointerStart) {
            pointerStart.maxDx = Math.max(
              pointerStart.maxDx,
              Math.abs(event.clientX - pointerStart.x)
            );
            pointerStart.maxDy = Math.max(
              pointerStart.maxDy,
              Math.abs(event.clientY - pointerStart.y)
            );
            const pricePanThreshold = pointerStart.pointerType === 'touch' ? 12 : 4;
            if (pointerStart.maxDy >= pricePanThreshold) {
              priceGestureChanged = true;
              releasePriceAutoScale();
            }
          }
          pointerIntentUntilRef.current = performance.now() + USER_INTENT_MS * 2;
          if (pointerGestureTypeRef.current === 'zoom') markZoomIntent();
          else markPanIntent();
        };
        const markTouchZoomIntent = (event) => {
          const touchCount = event.touches?.length ?? 0;
          if (touchCount < 2) {
            if (touchCount === 1 && pinchAwaitingInspection) {
              pinchAwaitingInspection = false;
              pointerGestureTypeRef.current = 'pan';
              markPanIntent();
            }
            return;
          }
          pinchAwaitingInspection = false;
          touchZoomActive = true;
          pointerGestureActive = true;
          gestureActiveRef.current = true;
          pointerIntentUntilRef.current = performance.now() + USER_INTENT_MS * 2;
          pointerGestureTypeRef.current = 'zoom';
          markZoomIntent();
        };
        const settleTouchZoom = (event) => {
          const touchCount = event.touches?.length ?? 0;
          if (touchCount >= 2 && touchZoomActive) {
            markTouchZoomIntent(event);
            return;
          }
          if (!touchZoomActive) {
            if (touchCount === 0) pinchAwaitingInspection = false;
            return;
          }
          touchZoomActive = false;
          pinchAwaitingInspection = touchCount === 1;
          settleDrag();
        };
        const cancelTouchGesture = () => {
          pinchAwaitingInspection = false;
          if (!touchZoomActive) return;
          touchZoomActive = false;
          settleDrag();
        };
        frameDocument.addEventListener('wheel', settleWheel, { capture: true, passive: true });
        frameDocument.addEventListener('dblclick', resetPriceAxis, { capture: true });
        frameDocument.addEventListener('click', activateLatestButton, { capture: true });
        frameDocument.addEventListener('pointerdown', markPointerIntent, { capture: true });
        frameDocument.addEventListener('pointermove', markOnDrag, { capture: true });
        frameDocument.addEventListener('pointerup', settleDrag, { capture: true });
        frameDocument.addEventListener('pointercancel', settleDrag, { capture: true });
        frameDocument.addEventListener('touchstart', markTouchZoomIntent, { capture: true, passive: true });
        frameDocument.addEventListener('touchmove', markTouchZoomIntent, { capture: true, passive: true });
        frameDocument.addEventListener('touchend', settleTouchZoom, { capture: true, passive: true });
        frameDocument.addEventListener('touchcancel', cancelTouchGesture, { capture: true, passive: true });
        disposers.push(() => {
          frameDocument.removeEventListener('wheel', settleWheel, { capture: true });
          frameDocument.removeEventListener('dblclick', resetPriceAxis, { capture: true });
          frameDocument.removeEventListener('click', activateLatestButton, { capture: true });
          frameDocument.removeEventListener('pointerdown', markPointerIntent, { capture: true });
          frameDocument.removeEventListener('pointermove', markOnDrag, { capture: true });
          frameDocument.removeEventListener('pointerup', settleDrag, { capture: true });
          frameDocument.removeEventListener('pointercancel', settleDrag, { capture: true });
          frameDocument.removeEventListener('touchstart', markTouchZoomIntent, { capture: true });
          frameDocument.removeEventListener('touchmove', markTouchZoomIntent, { capture: true });
          frameDocument.removeEventListener('touchend', settleTouchZoom, { capture: true });
          frameDocument.removeEventListener('touchcancel', cancelTouchGesture, { capture: true });
        });
      }
    } catch { /* cross-origin iframe - the widget events below still apply */ }

    const settleWindowBlur = () => {
      const chartFrame = chartContainerRef.current?.querySelector('iframe');
      if (chartFrame && document.activeElement === chartFrame) return;
      settleDrag();
    };
    window.addEventListener('pointerup', settleDrag, true);
    window.addEventListener('pointercancel', settleDrag, true);
    window.addEventListener('blur', settleWindowBlur);
    disposers.push(() => {
      window.removeEventListener('pointerup', settleDrag, true);
      window.removeEventListener('pointercancel', settleDrag, true);
      window.removeEventListener('blur', settleWindowBlur);
    });

    try {
      // Marking on mouse_up too keeps the window open past the release, since
      // the range settles after the gesture ends.
      const markWidgetDragIntent = () => {
        pointerGestureActive = true;
        gestureActiveRef.current = true;
        if (latestActionActive) return;
        if (performance.now() > pointerIntentUntilRef.current) {
          pointerGestureTypeRef.current = null;
          markUnknownDragIntent();
        } else {
          markUserIntent();
        }
      };
      widget.subscribe('mouse_down', markWidgetDragIntent);
      widget.subscribe('mouse_up', settleDrag);
      disposers.push(() => {
        try { widget.unsubscribe('mouse_down', markWidgetDragIntent); } catch { /* non-fatal */ }
        try { widget.unsubscribe('mouse_up', settleDrag); } catch { /* non-fatal */ }
      });
    } catch { /* API drift between library builds - non-fatal */ }

    return () => {
      for (const dispose of disposers) dispose();
    };
  }, [
    applyTimeWindowMode,
    flushPendingSharedPriceWindow,
    resetPriceWindow,
    schedulePricePublish,
    scheduleRangeCorrection,
  ]);

  const dynamicLevels = useGreeksWebsocketDataStore((state) => state.dynamicLevels);
  const topOptions = useGreeksWebsocketDataStore((state) => state.topOptions);
  const greekExposures = useGreeksWebsocketDataStore((state) => state.greekExposures);
  const gexTrendStrength = useGreeksWebsocketDataStore(
    (state) => state.gexTrendStrength
  );
  chartStudyInputsRef.current = {
    dynamicLevels,
    topOptions,
    greekExposures,
    gexTrendStrength,
  };

  useEffect(() => {
    if (!tvWidgetRef.current || !readyPRef.current) return;

    let cancelled = false;

    readyPRef.current.then((chart) => {
      if (cancelled || !chart) return;

      handleIndicatorReloadCheck({
        chart,
        dynamicLevels,
        topOptions,
        prevDynamicLevelsRef,
        prevTopOptionsRef,
      });
    });

    return () => {
      cancelled = true;
    };
  }, [dynamicLevels, topOptions]);

  useEffect(() => {
    const available = {
      totalGex: Array.isArray(greekExposures?.spx) && greekExposures.spx.some(
        (exposure) => exposure?.greek_type === 'gamma',
      ),
      gts: Array.isArray(gexTrendStrength?.spx) && gexTrendStrength.spx.length > 0,
    };
    const previous = greekStudyAvailabilityRef.current;
    greekStudyAvailabilityRef.current = available;
    const historyArrived = (
      (!previous.totalGex && available.totalGex)
      || (!previous.gts && available.gts)
    );
    const readyPromise = readyPRef.current;
    if (!historyArrived || !readyPromise) return undefined;

    let cancelled = false;
    let dataReadyTimer = null;
    readyPromise.then((chart) => {
      if (cancelled || readyPRef.current !== readyPromise || !chart) return;
      removeFocusedMinuteShape();
      try {
        resetChartData(chart);
      } catch { /* non-fatal TradingView API drift */ }
      dataReadyTimer = setTimeout(() => {
        dataReadyTimer = null;
        if (cancelled || readyPRef.current !== readyPromise) return;
        try {
          chart.dataReady(() => {
            if (!cancelled && readyPRef.current === readyPromise) {
              applySharedPriceWindowWhenIdle(chart);
              syncFocusedMinuteShapeRef.current(chart);
            }
          });
        } catch { /* non-fatal TradingView API drift */ }
      }, 0);
    });

    return () => {
      cancelled = true;
      if (dataReadyTimer) clearTimeout(dataReadyTimer);
    };
  }, [
    applySharedPriceWindowWhenIdle,
    greekExposures,
    gexTrendStrength,
    removeFocusedMinuteShape,
    resetChartData,
  ]);

  useEffect(() => {
    if (!isActive || !readyPRef.current) return undefined;

    let cancelled = false;
    let settleTimer = null;
    let priceSettleRaf = null;
    let resizeObserver = null;
    const id = requestAnimationFrame(() => {
      readyPRef.current?.then((chart) => {
        if (cancelled || !chart) return;

        const restorePriceAfterLayout = () => {
          priceSettleRaf = requestAnimationFrame(() => {
            priceSettleRaf = requestAnimationFrame(() => {
              priceSettleRaf = null;
              if (!cancelled) applySharedPriceWindowWhenIdle(chart);
            });
          });
        };

        // The bundled widget observes its container and can restore auto-scale
        // after this effect begins. Wait for that resize delivery and two paint
        // frames before restoring the shared vertical intent.
        if (typeof window.ResizeObserver === 'function' && chartContainerRef.current) {
          resizeObserver = new window.ResizeObserver(([entry]) => {
            if (entry?.contentRect?.width <= 0 || entry?.contentRect?.height <= 0) return;
            resizeObserver?.disconnect();
            resizeObserver = null;
            restorePriceAfterLayout();
          });
          resizeObserver.observe(chartContainerRef.current);
        } else {
          restorePriceAfterLayout();
        }

        // A valid shared selection outranks the activation reset. Without this
        // gate, returning to the tab silently erased the user's range.
        if (
          !timeViewDetachedRef.current
          && !applySharedWindow(chart, { force: true })
          && !applyDefaultLatestWindow(chart)
        ) {
          // The reset writes a range asynchronously, so suppress for the full
          // settle window and then record whatever it produced. Without this the
          // reset was reported back as a user range change and overwrote both
          // Bookmap tabs on every return to this tab.
          markProgrammaticRange(RESET_SETTLE_MS);
          try {
            chart.executeActionById('timeScaleReset');
          } catch { /* non-fatal */ }

          try {
            const ts = chart.getTimeScale();
            ts.usePercentageRightOffset().setValue(false);
            ts.defaultRightOffset().setValue(MAX_RIGHT_OFFSET_BARS);
            ts.setRightOffset(MAX_RIGHT_OFFSET_BARS);
          } catch { /* non-fatal */ }

          // The reset lands asynchronously, so reading back now would record the
          // pre-reset range and leave the echo check matching the wrong value.
          // Drop the stale entry immediately and re-read once the reset and the
          // saved-view restore have both settled.
          appliedRangeRef.current = null;
          setAcceptedRange(null);
          settleTimer = setTimeout(() => {
            settleTimer = null;
            if (!cancelled) noteAppliedRange(chart);
          }, RESET_SETTLE_MS - 200);
        }

        clampRightOffset(chart);
      });
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(id);
      if (priceSettleRaf) cancelAnimationFrame(priceSettleRaf);
      resizeObserver?.disconnect();
      if (settleTimer) clearTimeout(settleTimer);
    };
  }, [
    applyDefaultLatestWindow,
    applySharedPriceWindowWhenIdle,
    applySharedWindow,
    clampRightOffset,
    isActive,
    markProgrammaticRange,
    noteAppliedRange,
    setAcceptedRange,
  ]);

  useEffect(() => {
    const spxIndexBars =
      (typeof getIndexData === 'function' ? getIndexData('SPX') : indexData?.SPX) ||
      [];

    const hasAllIndexData =
      Array.isArray(spxIndexBars) && spxIndexBars.length > 0;

    // Mount on price bars alone. Greeks gate only the level-study overlays, which
    // populate when greeks arrive (handleIndicatorReloadCheck). Requiring greeks
    // here left the whole chart blank after hours even though last-trade-day price
    // bars are hydrated from cache - inconsistent with the heatmap/chain tiles.
    // A replay gap is valid data absence, not a reason to tear down the chart.
    // Keeping it mounted lets resetData show an empty span and load bars again
    // at the next cutoff without recreating the TradingView widget.
    setIsDataReady(hasAllIndexData || replaying || Boolean(tvWidgetRef.current));
  }, [getIndexData, indexData?.SPX, replaying]);

  useEffect(() => {
    if (!isDataReady || chartLibraryReady) return undefined;

    const detectLibrary = () => {
      if (window.TradingView) setChartLibraryReady(true);
    };
    detectLibrary();
    const timer = setInterval(detectLibrary, 250);
    return () => clearInterval(timer);
  }, [chartLibraryReady, isDataReady]);

  const retryChartInitialization = useCallback(() => {
    removeFocusedMinuteShape();
    try {
      tvWidgetRef.current?.remove?.();
    } catch { /* non-fatal TradingView cleanup drift */ }
    tvWidgetRef.current = null;
    readyPRef.current = null;
    chartContainerRef.current?.replaceChildren();
    setChartInitError(null);
    setChartLibraryReady(Boolean(window.TradingView));
    setChartInitAttempt((attempt) => attempt + 1);
  }, [removeFocusedMinuteShape]);

  useEffect(() => {
    if (!isDataReady || tvWidgetRef.current) {
      return;
    }
    if (!chartLibraryReady || !window.TradingView) {
      return;
    }

    const studyDisposers = studyDisposersRef.current;
    const rightEdgeTimers = rightEdgeTimersRef.current;
    const windowVerifyTimers = windowVerifyTimersRef.current;
    const chartContainer = chartContainerRef.current;
    const widgetEventDisposers = [];
    let disposed = false;
    let chartGeneration = 0;
    let chartLoadPending = false;
    let chartLoadRecoveryAttempts = 0;
    let chartLoadRecoveryTimer = null;
    let boundChart = null;

    const disposeChartBindings = () => {
      chartGeneration += 1;
      clearPendingLiveMinuteReanchor();
      for (const timer of rightEdgeTimers) clearTimeout(timer);
      rightEdgeTimers.clear();
      for (const timer of windowVerifyTimers) clearTimeout(timer);
      windowVerifyTimers.clear();
      if (gestureCorrectionTimerRef.current) {
        clearTimeout(gestureCorrectionTimerRef.current);
        gestureCorrectionTimerRef.current = null;
      }
      for (const dispose of studyDisposers) {
        try {
          dispose();
        } catch { /* non-fatal TradingView cleanup drift */ }
      }
      studyDisposers.clear();
    };

    const bindChart = (widget, chart, { initialize = false } = {}) => {
      const generation = chartGeneration;
      const bindingIsActive = () => !disposed && generation === chartGeneration;
      const register = (dispose) => {
        if (!bindingIsActive()) dispose();
        else studyDisposers.add(dispose);
      };

      if (initialize) {
        const studyInputs = chartStudyInputsRef.current;
        register(loadDefaultIndicators({
          chart,
          symbol: chart.symbol(),
          ...studyInputs,
        }));
      }

      register(attachSymbolChangeHandler({
        chart,
        getStudyInputs: () => chartStudyInputsRef.current,
      }));
      register(attachGestureListeners(widget, chart));

      const applyWindowOrRightEdge = () => {
        if (!timeViewDetachedRef.current && !applySharedWindow(chart)) {
          applyDefaultLatestWindow(chart);
        }
        clampRightOffset(chart);
      };

      if (initialize) {
        markProgrammaticRange(RESET_SETTLE_MS);
        chart.executeActionById('timeScaleReset');
      }

      chart.dataReady(() => {
        if (!bindingIsActive()) return;
        if (initialize) applyWindowOrRightEdge();
        else noteAppliedRange(chart);
        applySharedPriceWindow(chart);
        syncFocusedMinuteShapeRef.current(chart);

        try {
          const moved = chart.crossHairMoved?.();
          if (moved?.subscribe) {
            const token = {};
            moved.subscribe(token, (point) => {
              if (!bindingIsActive() || !isActiveRef.current) return;
              const time = Number(point?.time);
              const focus = useWidgetMinuteFocusStore.getState();
              if (!Number.isFinite(time)) {
                focus.clearFocusedMinute('chart');
                return;
              }
              focus.setFocusedMinute('chart', time > 1e12 ? time : time * 1000);
            });
            register(() => {
              try { moved.unsubscribe(token); } catch { /* non-fatal */ }
            });
          }
        } catch { /* API drift between library builds - non-fatal */ }

        try {
          const changed = chart.onVisibleRangeChanged?.();
          if (changed?.subscribe) {
            const token = {};
            changed.subscribe(token, () => {
              if (!bindingIsActive()) return;
              schedulePublish(chart);
              scheduleRangeCorrection(chart, USER_INTENT_MS);
              syncFocusedMinuteShapeRef.current(chart);
            });
            register(() => {
              try { changed.unsubscribe(token); } catch { /* non-fatal */ }
            });
          }
        } catch { /* API drift between library builds - non-fatal */ }

        try {
          const timeScale = chart.getTimeScale();
          const offsetChanged = timeScale.rightOffsetChanged?.();
          if (offsetChanged?.subscribe) {
            const token = {};
            offsetChanged.subscribe(token, () => {
              if (!bindingIsActive()) return;
              const userIntentActive = performance.now() <= userIntentUntilRef.current;
              if (isProgrammaticChange() && !userIntentActive) return;
              scheduleRangeCorrection(chart, USER_INTENT_MS);
            });
            register(() => {
              try { offsetChanged.unsubscribe(token); } catch { /* non-fatal */ }
            });
          }
        } catch { /* API drift between library builds - non-fatal */ }
      });

      if (initialize) {
        const rightEdgeTimer = setTimeout(() => {
          rightEdgeTimers.delete(rightEdgeTimer);
          if (bindingIsActive()) applyWindowOrRightEdge();
        }, 1500);
        rightEdgeTimers.add(rightEdgeTimer);
      }
    };

    const applyPendingHistoryRepair = (chart) => {
      const readyPromise = readyPRef.current;
      const version = pendingHistoryVersionRef.current;
      const stale = version !== null && version < seenHistoryVersionRef.current;
      const alreadyApplied = version !== null
        && version === seenHistoryVersionRef.current
        && seenHistoryChartRef.current === chart;
      const pendingAnchor = pendingHistoryAnchorRef.current;
      if (version === null || stale || alreadyApplied) {
        pendingHistoryVersionRef.current = null;
        if (
          version === null
          || (pendingAnchor && pendingAnchor.historyVersion <= version)
        ) {
          pendingHistoryAnchorRef.current = null;
        }
        flushDeferredReplayCutoff();
        return;
      }
      pendingHistoryVersionRef.current = null;
      const historyAnchor = pendingAnchor?.historyVersion === version
        && pendingAnchor.acceptedRevision === acceptedRangeRevisionRef.current
        ? pendingAnchor
        : null;
      seenHistoryVersionRef.current = version;
      seenHistoryChartRef.current = chart;
      removeFocusedMinuteShape();
      try {
        resetChartData(chart);
      } catch { /* non-fatal TradingView API drift */ }
      const timer = setTimeout(() => {
        rightEdgeTimers.delete(timer);
        if (disposed) return;
        try {
          chart.dataReady(() => {
            if (
              disposed
              || readyPRef.current !== readyPromise
              || seenHistoryChartRef.current !== chart
              || seenHistoryVersionRef.current !== version
            ) return;
            const activeHistoryAnchor = historyAnchor
              && historyAnchor.acceptedRevision === acceptedRangeRevisionRef.current
              ? historyAnchor
              : null;
            restoreHistoryRange(chart, activeHistoryAnchor?.previousLastBarMs, (applied) => {
              if (
                disposed
                || readyPRef.current !== readyPromise
                || seenHistoryChartRef.current !== chart
                || seenHistoryVersionRef.current !== version
              ) return;
              // A repaint whose range never landed leaves the anchor for the
              // next repair; the deferred scrub is released either way.
              if (applied && pendingHistoryAnchorRef.current === pendingAnchor) {
                pendingHistoryAnchorRef.current = null;
              }
              flushDeferredReplayCutoff();
            });
            applySharedPriceWindowWhenIdle(chart);
            syncFocusedMinuteShapeRef.current(chart);
          });
        } catch { /* non-fatal TradingView API drift */ }
      }, 0);
      rightEdgeTimers.add(timer);
    };

    const widgetOptions = buildWidgetOptions({
      chartContainerRef,
      indexData,
      dynamicLevels,
      topOptions,
      greekExposures,
      gexTrendStrength,
      isMobile,
    });
    dataFeedRef.current = widgetOptions.datafeed;

    try {
      setChartInitError(null);
      tvWidgetRef.current = new window.TradingView.widget(widgetOptions);
    } catch (error) {
      dataFeedRef.current?.destroy?.();
      dataFeedRef.current = null;
      tvWidgetRef.current = null;
      readyPRef.current = null;
      chartContainer?.replaceChildren();
      setChartInitError(error instanceof Error ? error : new Error('Chart initialization failed.'));
      return;
    }

    readyPRef.current = new Promise((resolve) => {
      tvWidgetRef.current.onChartReady(() => {
        if (disposed) return;
        const widget = tvWidgetRef.current;
        const chart = widget.chart();

        resolve(chart);

        applyChartStyling(widget);

        attachCustomButtons({
          widget,
          memberId,
          tvWidgetRef,
          isMobile,
        });

        let layoutLoadOperation = null;
        let loadBindingsDrained = false;
        let chartLoadedObserved = false;
        const settleLayoutLoad = (loadError) => {
          const operation = layoutLoadOperation;
          if (!operation) return;
          layoutLoadOperation = null;
          if (loadError) operation.reject(loadError);
          else operation.resolve();
        };
        const clearChartLoadRecovery = () => {
          if (!chartLoadRecoveryTimer) return;
          clearTimeout(chartLoadRecoveryTimer);
          chartLoadRecoveryTimer = null;
        };
        const rebindActiveChart = (candidateChart, { completeLoad = true } = {}) => {
          if (disposed || !chartLoadPending) return false;
          try {
            const loadedChart = candidateChart || widget.activeChart?.() || widget.chart();
            disposeChartBindings();
            boundChart = null;
            appliedRangeRef.current = null;
            setAcceptedRange(null);
            readyPRef.current = Promise.resolve(loadedChart);
            bindChart(widget, loadedChart);
            applyPendingHistoryRepair(loadedChart);
            boundChart = loadedChart;
            loadBindingsDrained = false;
            setChartInitError(null);
            if (completeLoad) {
              chartLoadPending = false;
              chartLoadedObserved = false;
              chartLoadRecoveryAttempts = 0;
              clearChartLoadRecovery();
              settleLayoutLoad();
            }
            return true;
          } catch {
            readyPRef.current = null;
            return false;
          }
        };
        const scheduleChartLoadRecovery = () => {
          clearChartLoadRecovery();
          chartLoadRecoveryTimer = setTimeout(() => {
            chartLoadRecoveryTimer = null;
            const rebound = rebindActiveChart(undefined, {
              completeLoad: chartLoadedObserved,
            });
            if (chartLoadPending && !disposed) {
              chartLoadRecoveryAttempts += 1;
              if (chartLoadRecoveryAttempts >= MAX_CHART_LOAD_RECOVERY_ATTEMPTS) {
                const recoveryError = new Error(
                  'The chart could not recover after loading the saved layout.',
                );
                chartLoadPending = false;
                chartLoadedObserved = false;
                if (!rebound) {
                  loadBindingsDrained = false;
                  disposeChartBindings();
                  boundChart = null;
                  setChartInitError(recoveryError);
                }
                settleLayoutLoad(recoveryError);
              } else {
                scheduleChartLoadRecovery();
              }
            }
          }, CHART_LOAD_RECOVERY_MS);
        };
        const handleChartLoadRequested = () => {
          if (disposed) return;
          clearChartLoadRecovery();
          chartLoadedObserved = false;
          disposeChartBindings();
          loadBindingsDrained = true;
          removeFocusedMinuteShape();
          readyPRef.current = null;
          boundChart = null;
          chartLoadPending = true;
          chartLoadRecoveryAttempts = 0;
          const latestVersion = latestHistoryVersionRef.current;
          if (seenHistoryVersionRef.current !== latestVersion) {
            pendingHistoryVersionRef.current = Math.max(
              pendingHistoryVersionRef.current ?? latestVersion,
              latestVersion,
            );
          }
          scheduleChartLoadRecovery();
        };
        const handleChartLoaded = () => {
          if (disposed) return;
          let activeChart;
          try {
            activeChart = widget.activeChart?.() || widget.chart();
          } catch {
            return;
          }
          chartLoadedObserved = true;
          if (!chartLoadPending && activeChart === boundChart) return;
          if (
            boundChart
            && activeChart !== boundChart
            && seenHistoryChartRef.current === boundChart
          ) {
            pendingHistoryVersionRef.current = Math.max(
              pendingHistoryVersionRef.current ?? seenHistoryVersionRef.current,
              seenHistoryVersionRef.current,
            );
          }
          if (!chartLoadPending) {
            chartLoadPending = true;
            chartLoadRecoveryAttempts = 0;
            scheduleChartLoadRecovery();
          }
          rebindActiveChart(activeChart);
        };
        const unregisterLayoutLoadController = registerWidgetLayoutLoadController(
          widget,
          (content) => {
            if (layoutLoadOperation || chartLoadPending) {
              return Promise.reject(new Error('A saved layout is already loading.'));
            }
            chartLoadPending = true;
            chartLoadedObserved = false;
            chartLoadRecoveryAttempts = 0;
            loadBindingsDrained = false;
            scheduleChartLoadRecovery();
            return new Promise((resolveLoad, rejectLoad) => {
              layoutLoadOperation = { resolve: resolveLoad, reject: rejectLoad };
              try {
                widget.load(content);
              } catch (loadError) {
                const normalizedError = loadError instanceof Error
                  ? loadError
                  : new Error('Failed to load the saved layout.');
                settleLayoutLoad(normalizedError);
                if (!loadBindingsDrained) {
                  chartLoadPending = false;
                  clearChartLoadRecovery();
                } else if (rebindActiveChart(undefined, { completeLoad: false })) {
                  chartLoadPending = false;
                  clearChartLoadRecovery();
                }
              }
            });
          },
        );
        widget.subscribe('chart_load_requested', handleChartLoadRequested);
        widget.subscribe('chart_loaded', handleChartLoaded);
        widgetEventDisposers.push(() => {
          try { widget.unsubscribe('chart_load_requested', handleChartLoadRequested); } catch { /* non-fatal */ }
          try { widget.unsubscribe('chart_loaded', handleChartLoaded); } catch { /* non-fatal */ }
          unregisterLayoutLoadController();
          settleLayoutLoad(new Error('The chart was removed before the saved layout loaded.'));
        });

        bindChart(widget, chart, { initialize: true });
        boundChart = chart;
      });
    });
    const initialReadyPromise = readyPRef.current;
    initialReadyPromise.then((chart) => {
      if (
        disposed
        || readyPRef.current !== initialReadyPromise
        || boundChart !== chart
      ) return;
      applyPendingHistoryRepair(chart);
    });

    return () => {
      disposed = true;
      if (chartLoadRecoveryTimer) clearTimeout(chartLoadRecoveryTimer);
      disposeChartBindings();
      for (const dispose of widgetEventDisposers) dispose();

      dataFeedRef.current?.destroy?.();
      dataFeedRef.current = null;

      if (tvWidgetRef.current) {
        removeFocusedMinuteShape();
        try {
          tvWidgetRef.current.remove();
        } catch { /* non-fatal TradingView cleanup drift */ }
        tvWidgetRef.current = null;
      }

      readyPRef.current = null;
      chartContainer?.replaceChildren();
    };
    // Data inputs update studies after mount. Remounting on each tick loses the
    // TradingView instance and its user state (TD050).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    applySharedWindow,
    applySharedPriceWindow,
    applySharedPriceWindowWhenIdle,
    applyDefaultLatestWindow,
    chartInitAttempt,
    chartLibraryReady,
    clampRightOffset,
    isDataReady,
    isProgrammaticChange,
    markProgrammaticRange,
    schedulePublish,
    scheduleRangeCorrection,
    removeFocusedMinuteShape,
    resetChartData,
    clearPendingLiveMinuteReanchor,
    setAcceptedRange,
  ]);

  useEffect(() => {
    sharedWindowRef.current = sharedWindow;
    // The price chart stays mounted behind other tabs, so a Bookmap selection
    // has to land now rather than waiting for the next activation.
    if (!sharedWindow || sharedWindowSource === WINDOW_SOURCE_ID) return;
    if (timeViewDetachedRef.current && sharedWindowSource === 'replay') return;
    updateTimeViewDetached(false);
    readyPRef.current?.then((chart) => {
      if (chart) applySharedWindow(chart);
    });
  }, [applySharedWindow, sharedWindow, sharedWindowSource, updateTimeViewDetached]);

  useEffect(() => {
    let cancelled = false;
    readyPRef.current?.then((chart) => {
      if (!cancelled && chart) applySharedPriceWindowWhenIdle(chart);
    });
    return () => { cancelled = true; };
  }, [
    applySharedPriceWindowWhenIdle,
    lastBarMs,
    sharedPriceWindow,
    sharedPriceWindowSource,
  ]);

  // A positive right offset makes native new-bar follow unreliable. Re-resolve
  // the stored anchor when a new live minute arrives.
  useEffect(() => {
    const previousLastBarMs = Number(previousLastBarMsRef.current);
    const currentLastBarMs = Number(lastBarMs);
    previousLastBarMsRef.current = lastBarMs;
    if (seenHistoryVersionRef.current !== indexHistoryVersion) {
      const acceptedRevision = acceptedRangeRevisionRef.current;
      const pendingAnchor = pendingHistoryAnchorRef.current;
      if (pendingAnchor?.acceptedRevision === acceptedRevision) {
        pendingHistoryAnchorRef.current = {
          ...pendingAnchor,
          historyVersion: indexHistoryVersion,
        };
      } else if (
        Number.isFinite(previousLastBarMs)
        && Number.isFinite(currentLastBarMs)
        && currentLastBarMs > previousLastBarMs
      ) {
        pendingHistoryAnchorRef.current = {
          historyVersion: indexHistoryVersion,
          previousLastBarMs,
          acceptedRevision,
        };
      }
    }
    if (Number.isFinite(replayCutoffMs)) {
      clearPendingLiveMinuteReanchor();
      return;
    }
    if (
      !Number.isFinite(previousLastBarMs)
      || !Number.isFinite(currentLastBarMs)
      || currentLastBarMs <= previousLastBarMs
    ) return;

    let cancelled = false;
    readyPRef.current?.then((chart) => {
      if (cancelled || !chart) return;
      if (
        gestureActiveRef.current
        || publishRafRef.current
        || performance.now() <= userIntentUntilRef.current
      ) {
        if (!pendingLiveMinuteReanchorRef.current) {
          pendingLiveMinutePreviousBarRef.current = previousLastBarMs;
        }
        pendingLiveMinuteReanchorRef.current = true;
        schedulePendingLiveMinuteReanchor(chart);
        return;
      }
      reanchorLiveMinute(chart, previousLastBarMs);
    });
    return () => {
      cancelled = true;
    };
  }, [
    indexHistoryVersion,
    lastBarMs,
    clearPendingLiveMinuteReanchor,
    reanchorLiveMinute,
    replayCutoffMs,
    schedulePendingLiveMinuteReanchor,
  ]);

  useEffect(() => {
    const requestedCutoffMs = replayCutoffMs;
    if (seenHistoryVersionRef.current !== indexHistoryVersion) {
      if (appliedReplayCutoffRef.current !== requestedCutoffMs) {
        deferredReplayCutoffRef.current = true;
      }
      return undefined;
    }
    appliedReplayCutoffRef.current = requestedCutoffMs;
    deferredReplayCutoffRef.current = false;
    let cancelled = false;
    let dataReadyTimer = null;
    readyPRef.current?.then((chart) => {
      if (cancelled || usePlaybackStore.getState().replayCutoffMs !== requestedCutoffMs) return;
      removeFocusedMinuteShape();
      try {
        resetChartData(chart);
      } catch { /* non-fatal TradingView API drift */ }
      dataReadyTimer = setTimeout(() => {
        dataReadyTimer = null;
        if (cancelled || usePlaybackStore.getState().replayCutoffMs !== requestedCutoffMs) return;
        try {
          chart.dataReady(() => {
            if (cancelled || usePlaybackStore.getState().replayCutoffMs !== requestedCutoffMs) return;
            if (Number.isFinite(requestedCutoffMs) && !timeViewDetachedRef.current) {
              sharedWindowRef.current = useChartTimeWindowStore.getState().preference;
              applySharedWindow(chart, { force: true });
            }
            applySharedPriceWindowWhenIdle(chart);
            syncFocusedMinuteShapeRef.current(chart);
          });
        } catch { /* non-fatal TradingView API drift */ }
      }, 0);
    });
    return () => {
      cancelled = true;
      if (dataReadyTimer) clearTimeout(dataReadyTimer);
    };
  }, [
    applySharedPriceWindowWhenIdle,
    applySharedWindow,
    indexHistoryVersion,
    removeFocusedMinuteShape,
    replayCutoffMs,
    replayCutoffRetry,
    resetChartData,
  ]);

  // A history payload replaced the series -- a reconnect or a resync repairing a
  // stretch this browser missed. The library caches what getBars already
  // returned, so without this the repaired bars sit in the store unrendered and
  // the chart still ends where the gap began. Skipped on the first render: the
  // initial load is the chart's own fetch, not a repair.
  useEffect(() => {
    if (seenHistoryVersionRef.current === indexHistoryVersion) return;
    const readyPromise = readyPRef.current;
    if (!readyPromise) {
      pendingHistoryVersionRef.current = Math.max(
        pendingHistoryVersionRef.current ?? indexHistoryVersion,
        indexHistoryVersion,
      );
      flushDeferredReplayCutoff();
      return;
    }
    let cancelled = false;
    let dataReadyTimer = null;
    readyPromise.then((chart) => {
      if (cancelled) return;
      if (readyPRef.current !== readyPromise) {
        if (seenHistoryVersionRef.current !== indexHistoryVersion) {
          pendingHistoryVersionRef.current = Math.max(
            pendingHistoryVersionRef.current ?? indexHistoryVersion,
            indexHistoryVersion,
          );
        }
        flushDeferredReplayCutoff();
        return;
      }
      seenHistoryVersionRef.current = indexHistoryVersion;
      seenHistoryChartRef.current = chart;
      if (pendingHistoryVersionRef.current === indexHistoryVersion) {
        pendingHistoryVersionRef.current = null;
      }
      const pendingAnchor = pendingHistoryAnchorRef.current;
      const historyAnchor = pendingAnchor?.historyVersion === indexHistoryVersion
        && pendingAnchor.acceptedRevision === acceptedRangeRevisionRef.current
        ? pendingAnchor
        : null;
      removeFocusedMinuteShape();
      try {
        resetChartData(chart);
      } catch { /* non-fatal TradingView API drift */ }
      dataReadyTimer = setTimeout(() => {
        dataReadyTimer = null;
        if (cancelled) return;
        try {
          chart.dataReady(() => {
            if (
              cancelled
              || readyPRef.current !== readyPromise
              || seenHistoryChartRef.current !== chart
              || seenHistoryVersionRef.current !== indexHistoryVersion
            ) return;
            const activeHistoryAnchor = historyAnchor
              && historyAnchor.acceptedRevision === acceptedRangeRevisionRef.current
              ? historyAnchor
              : null;
            restoreHistoryRange(chart, activeHistoryAnchor?.previousLastBarMs, (applied) => {
              if (
                cancelled
                || readyPRef.current !== readyPromise
                || seenHistoryChartRef.current !== chart
                || seenHistoryVersionRef.current !== indexHistoryVersion
              ) return;
              // A repaint whose range never landed leaves the anchor for the
              // next repair; the deferred scrub is released either way.
              if (applied && pendingHistoryAnchorRef.current === pendingAnchor) {
                pendingHistoryAnchorRef.current = null;
              }
              flushDeferredReplayCutoff();
            });
            applySharedPriceWindowWhenIdle(chart);
            syncFocusedMinuteShapeRef.current(chart);
          });
        } catch { /* non-fatal TradingView API drift */ }
      }, 0);
    });
    return () => {
      cancelled = true;
      if (dataReadyTimer) clearTimeout(dataReadyTimer);
    };
  }, [
    applySharedPriceWindowWhenIdle,
    flushDeferredReplayCutoff,
    indexHistoryVersion,
    removeFocusedMinuteShape,
    resetChartData,
    restoreHistoryRange,
  ]);

  const displayStatus =
    isDataReady && !chartLibraryReady && !chartInitError
      ? 'loading'
      : dataState.status;
  const activeTimeMode = timeViewDetached || isCustomWindowPreference(sharedWindow)
    ? WINDOW_MODE.ABSOLUTE
    : sharedWindow?.mode ?? WINDOW_MODE.LATEST;
  const timeModesActionable = indexBars.length > 1;

  return (
    <div
      className="chart-host"
      onPointerLeave={() => useWidgetMinuteFocusStore.getState().clearFocusedMinute('chart')}
    >
      <WidgetPanelDataStatus status={displayStatus} asOf={dataState.asOf} />
      <WidgetPanelControls className="chart-time-window-controls">
        <label className="chart-time-window-control">
          <select
            value={activeTimeMode}
            disabled={!timeModesActionable}
            aria-label="Time window"
            onChange={(event) => {
              const nextMode = event.target.value;
              if (nextMode !== activeTimeMode) applyTimeWindowMode(nextMode);
            }}
          >
            {activeTimeMode === WINDOW_MODE.ABSOLUTE && (
              <option value={WINDOW_MODE.ABSOLUTE} disabled>Custom</option>
            )}
            <option value={WINDOW_MODE.SESSION}>Session</option>
            <option value={WINDOW_MODE.LATEST}>Latest</option>
          </select>
        </label>
      </WidgetPanelControls>
      {(displayStatus === 'loading' ||
        displayStatus === 'empty' ||
        displayStatus === 'error') && (
        <div className="chart-host__state">
          <DataStateBadge
            status={displayStatus}
            asOf={dataState.asOf}
            message={chartInitError?.message}
            onRetry={displayStatus === 'error' ? retryChartInitialization : undefined}
          />
        </div>
      )}
      <div
        ref={chartContainerRef}
        className="chart-host__canvas"
        aria-hidden={displayStatus === 'error'}
      />
    </div>
  );
};

export default AdvancedFlowsDashboard;
